/**
 * TimeGate.jsx — Round 16: the time-card enforcement overlay.
 *
 * Blocks Cortex for a member who hasn't accounted for their prior work day. Two modes (server-decided):
 *   * 'hours'  (full-time employees) — each outstanding day shows submitted-vs-required; the user opens
 *              the Time Card to enter/submit. Satisfied days auto-clear server-side on the next recheck.
 *   * 'attest' (contractors / part-timers) — per day, the user commits: worked (enter + confirm) or
 *              didn't work (clears, no PTO forced). Confirming locks the day; reopening needs an admin.
 *
 * Sits at z-index 200 — above the outline, below the Time Card modal (.tc-overlay z-720) so the user can
 * always open the Time Card to self-clear. Re-evaluates on the `time-gate-recheck` event (TimeCardModal
 * fires it after a submit) and on window focus. Mounted once in OutlineView.
 *
 * Round 17: everyone (not just admins) gets a personal, time-boxed "Snooze 1 hour" — bounded THREE
 * ways so it can't become perpetual delay: a firm-wide earliest time-of-day, a per-day use cap, and
 * a backlog cutoff that turns snoozing off entirely once too many days have piled up (server-
 * enforced, see api/time-gate.js). An earlier version also offered "Snooze until tomorrow" — removed
 * (Matt, 2026-07): even bounded to 2/day it could buy up to ~24 hours per click, and worse, a
 * disciplined once-a-day click was well under any daily cap, so the actual guarantee against
 * "forever" always had to come from the backlog cutoff, not the button's size. The separate admin
 * "Pause" is now an auto-expiring firm-wide pause, not a permanent kill switch — this fixes the
 * original failure mode where pausing once silently stayed off forever.
 */

import RoleChip from './RoleChip.jsx'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useClerk } from '@clerk/clerk-react'
import { ctx } from '../outline/ctx.js'
import { isSeatLoaded, hasModule } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { getGateStatus, attestDay, confirmComplete, pauseEnforcement, snoozeMe } from '../services/timeGate.js'
import TriageTiles from './TriageTiles.jsx'
import { lockScroll } from '../outline/scrollLock.js'
import { listExpenseReports } from '../services/expenseReports.js'

function fmtDay(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
}
function mondayOf(ymd) {
  const [y, m, d] = String(ymd).split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  const w = dt.getUTCDay()                 // 0 Sun … 6 Sat
  dt.setUTCDate(dt.getUTCDate() + (w === 0 ? -6 : 1 - w))
  return dt.toISOString().slice(0, 10)
}
function fmtWeek(ymd) { return 'Week of ' + fmtDay(mondayOf(ymd)) }
const hrs = (n) => (Math.round((Number(n) || 0) * 100) / 100)

// Reasons the user is structurally NOT subject to the gate — safe to trust from cache when the
// status request can't be reached (so we don't falsely block when the feature is off / unenrolled).
const SAFE_REASONS = ['disabled', 'no-policy', 'no-start']
const lastKey = (mid) => 'aj:timegate:last:' + mid

export default function TimeGate() {
  const [status, setStatus] = useState(null)     // { gated, mode, days } | null
  const [busy, setBusy] = useState('')            // date currently being acted on
  const [ready, setReady] = useState(false)
  const [verifyFailed, setVerifyFailed] = useState(false)  // couldn't reach the gate API → fail closed
  const [focusedIndex, setFocusedIndex] = useState(0)      // keyboard-selected row
  const [snoozeMsg, setSnoozeMsg] = useState('')           // inline feedback for a failed snooze
  const rowsRef = useRef([])
  const focusedRef = useRef(0)
  const pollRef = useRef(null)
  const { signOut } = useClerk()

  const memberId = ctx.currentMember && ctx.currentMember.id
  const personalDays = (status && Array.isArray(status.days)) ? status.days : []
  const reviewerGate = (status && status.reviewerGate) || { gated: false, byMember: [], total: 0, postFailed: { total: 0, byMember: [] }, expenses: { total: 0, byMember: [] } }
  // Waiting expenses hold the review card too (2026-10-28, api/time-gate.js#expensesDue).
  const gateExp = (reviewerGate.expenses && reviewerGate.expenses.byMember) || []
  const gatedNow = !!(status && status.gated && (personalDays.length || reviewerGate.gated))
  // A peek/split iframe (schedulePeek.js, splitView.js) loads a fresh copy of the app for the SAME
  // signed-in reviewer whose own pending time is what put them in Time Review in the first place —
  // without this, that fresh copy re-runs its own gate check and blocks the very view the reviewer
  // opened it to see (Matt, 2026-08-29). isEmbedded() is a fixed read of the `split` query param at
  // module load, so this never flips mid-lifetime — the polling/recheck effects below still run
  // (harmless), just never render the scrim.
  const blocking = !isEmbedded() && (gatedNow || verifyFailed)

  // Announce our own blocking state so ProgressGate.jsx can defer to it — Matt's call: time takes
  // priority, progress-update nagging shouldn't stack on top of (or race) a time-card block. Only
  // dispatched once the first real check has resolved (status set OR verifyFailed) — never announce
  // an unknown state as "not blocking", or ProgressGate could flash visible before we actually know.
  useEffect(() => {
    if (status !== null || verifyFailed) {
      window.dispatchEvent(new CustomEvent('time-gate-blocking-changed', { detail: { blocking } }))
    }
  }, [blocking, status, verifyFailed])

  // Flat list of keyboard-navigable rows (↑/↓ to select, Enter to open) — order matches the render:
  // personal days when present, otherwise reviewer rows then post-failed rows. Each row's open() is
  // the same action as clicking its primary button.
  const navRows = []
  if (status && status.gated) {
    if (personalDays.length) {
      personalDays.forEach(d => navRows.push({ key: 'p:' + d.date, open: () => window.dispatchEvent(new CustomEvent('open-timecard', { detail: { date: d.date } })) }))
    } else {
      (reviewerGate.byMember || []).forEach(m => navRows.push({ key: 'r:' + (m.memberId || m.memberName), open: () => window.dispatchEvent(new CustomEvent('open-timecard-review', { detail: { memberId: m.memberId || null, statuses: ['Submitted'], from: m.oldestDate || undefined } })) }))
      gateExp.filter(x => !(reviewerGate.byMember || []).some(m => m.memberId === x.memberId))
        .forEach(x => navRows.push({ key: 'x:' + x.memberId, open: () => window.dispatchEvent(new CustomEvent('open-expense-review', { detail: { memberId: x.memberId } })) }))
      ;(((reviewerGate.postFailed && reviewerGate.postFailed.byMember) || [])).forEach(m => navRows.push({ key: 'pf:' + (m.memberId || m.memberName), open: () => window.dispatchEvent(new CustomEvent('open-timecard-review', { detail: { memberId: m.memberId || null, statuses: ['Post Failed'], from: m.oldestDate || undefined } })) }))
    }
  }
  rowsRef.current = navRows
  focusedRef.current = focusedIndex
  const navSig = navRows.map(r => r.key).join('|')
  const idxByKey = {}; navRows.forEach((r, i) => { idxByKey[r.key] = i })

  // If the status request can't be reached (ad/content blocker, offline, server down) we FAIL CLOSED —
  // a blocked request must not grant access. We only let it pass when the last known good response
  // says the user is structurally not subject to the gate (feature disabled / not enrolled).
  const recheck = useCallback(async () => {
    const mid = ctx.currentMember && ctx.currentMember.id
    if (!isSeatLoaded() || !mid) return
    try {
      const s = await getGateStatus(mid)
      if (!s || typeof s.gated !== 'boolean') throw new Error('bad-response')
      try { localStorage.setItem(lastKey(mid), JSON.stringify(s)) } catch {}
      setStatus(s); setVerifyFailed(false)
    } catch {
      let cached = null
      try { cached = JSON.parse(localStorage.getItem(lastKey(mid)) || 'null') } catch {}
      if (cached && cached.gated === false && SAFE_REASONS.includes(cached.reason)) {
        setStatus(cached); setVerifyFailed(false)     // genuinely not subject to gating
      } else {
        setStatus(null); setVerifyFailed(true)        // unknown / was-gated / caught-up → block
      }
    }
  }, [])

  // Wait until seat + current member are resolved, then do the first check. Cap the wait so a user
  // without a Members record (never gated) doesn't poll forever.
  useEffect(() => {
    if (ready) return
    let tries = 0
    pollRef.current = setInterval(() => {
      tries++
      if (isSeatLoaded() && ctx.currentMember && ctx.currentMember.id) {
        clearInterval(pollRef.current); pollRef.current = null
        setReady(true); recheck()
      } else if (tries > 40) {  // ~16s — give up; no member ⇒ nothing to gate
        clearInterval(pollRef.current); pollRef.current = null
      }
    }, 400)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [ready, recheck])

  // Re-evaluate after the Time Card reports a submit, and on window focus.
  useEffect(() => {
    const onRecheck = () => recheck()
    window.addEventListener('time-gate-recheck', onRecheck)
    window.addEventListener('focus', onRecheck)
    return () => { window.removeEventListener('time-gate-recheck', onRecheck); window.removeEventListener('focus', onRecheck) }
  }, [recheck])

  // Lock the page behind the gate: stop window scroll (on <html>, independent of the Time Card
  // modal's own body-scroll toggling) while gated.
  useEffect(() => {
    if (!blocking) return
    return lockScroll()   // shared counter: the page scrolls again once every lockout has closed
  }, [blocking])

  // Each reviewee's waiting EXPENSES beside their time (Matt, 2026-10-28: the cards said "Review
  // Time" and showed only time entries). Read once the reviewer stage shows, and again after Expense
  // Review closes (it fires time-gate-recheck). Expenses inform; only time decides the lockout.
  const [expenses, setExpenses] = useState(null)   // [{ memberId, memberName, count, total }] | null
  const reviewStage = !!(blocking && status && status.gated && !personalDays.length && reviewerGate.gated)
  useEffect(() => {
    if (!reviewStage) return undefined
    let live = true
    const load = () => listExpenseReports('review').then(d => {
      if (!live) return
      const by = new Map()
      for (const x of (d && d.reports) || []) {
        const k = x.memberId || x.memberName || '?'
        const m = by.get(k) || { memberId: x.memberId || null, memberName: x.memberName || 'No filer recorded', count: 0, total: 0 }
        m.count++; m.total += Number(x.amount) || 0
        by.set(k, m)
      }
      setExpenses([...by.values()])
    }).catch(() => { if (live) setExpenses([]) })
    load()
    window.addEventListener('time-gate-recheck', load)
    return () => { live = false; window.removeEventListener('time-gate-recheck', load) }
  }, [reviewStage])

  // Reset the selection to the top whenever the row set changes (and on first show).
  useEffect(() => { setFocusedIndex(0) }, [navSig])

  // Arrow keys move the selected row; Enter opens it (same as clicking its primary button). Suppressed
  // while the Time Review / Time Card modal is open on top — those own the keyboard then.
  useEffect(() => {
    if (!blocking) return
    function onKey(e) {
      if (document.querySelector('.tr-overlay') || document.querySelector('.tc-overlay')) return
      const rows = rowsRef.current || []
      if (!rows.length) return
      const ae = document.activeElement
      if (ae && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName)) return
      if (e.key === 'ArrowDown') { e.preventDefault(); setFocusedIndex(i => Math.min(rows.length - 1, i + 1)) }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setFocusedIndex(i => Math.max(0, i - 1)) }
      else if (e.key === 'Enter') {
        if (ae && (ae.tagName === 'BUTTON' || ae.tagName === 'A')) return  // a focused control handles its own Enter
        const r = rows[focusedRef.current]; if (r) { e.preventDefault(); r.open() }
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [blocking])

  if (!blocking) return null

  // Couldn't verify (request blocked / offline / server error) → fail-closed panel.
  if (verifyFailed && !gatedNow) {
    return (
      <div style={S.scrim}>
        <div style={S.card}>
          <div style={S.eyebrow}>Verification required</div>
          <h2 style={S.title}>We couldn't verify your time card</h2>
          <hr style={S.rule} />
          <p style={S.body}>
            Cortex couldn't reach the time-card service. If you use an ad or content blocker, allow this
            site and try again. Check your connection if the problem continues.
          </p>
          <div style={S.rowActions}>
            <button style={S.primary} onClick={recheck}>Try again</button>
          </div>
          <div style={S.footer}>
            <button style={S.linkBtn} onClick={() => signOut()}>Sign out</button>
          </div>
        </div>
      </div>
    )
  }

  const mode = status.mode
  // Deep-link to a specific day. For a weekly row d.date is the Friday (the likely culprit).
  const openTimeCard = (date) => window.dispatchEvent(new CustomEvent('open-timecard', { detail: date ? { date } : undefined }))
  // Open Time Review filtered to this member's submitted entries (the ones the button refers to).
  const openTimeReview = (m, statuses, range) => window.dispatchEvent(new CustomEvent('open-timecard-review', {
    detail: {
      memberId: m && m.memberId ? m.memberId : null,
      statuses: statuses || ['Submitted'],
      from: range && range.from ? range.from : undefined,
    },
  }))
  const openExpenseReview = (m) => window.dispatchEvent(new CustomEvent('open-expense-review', { detail: { memberId: (m && m.memberId) || null } }))
  const expOf = (m) => (expenses || []).find(x => (x.memberId && x.memberId === m.memberId) || (!x.memberId && x.memberName === m.memberName)) || null
  const expOnly = gateExp.filter(g => !(reviewerGate.byMember || []).some(m => m.memberId === g.memberId))
    .map(g => (expenses || []).find(x => x.memberId === g.memberId) || { memberId: g.memberId, memberName: 'Team member', count: g.count, total: 0 })
  const expLine = (x) => `${x.count} expense${x.count === 1 ? '' : 's'} to review · $${x.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const hasPersonal = personalDays.length > 0
  const hasTimeReviews = !!(reviewerGate.byMember && reviewerGate.byMember.length)
  const hasReviews = hasTimeReviews || gateExp.length > 0
  const postFailed = reviewerGate.postFailed || { total: 0, byMember: [] }
  const hasPostFailed = !!(postFailed.byMember && postFailed.byMember.length)

  async function doAttest(date, outcome) {
    setBusy(date)
    try { await attestDay(memberId, date, outcome) } finally { setBusy(''); recheck() }
  }
  async function doConfirm(date) {
    setBusy(date)
    try { await confirmComplete(memberId, date) } finally { setBusy(''); recheck() }
  }
  async function doPause(hours) {
    setBusy('__pause__')
    try { await pauseEnforcement(hours) } finally { setBusy(''); recheck() }
  }
  async function doSnooze(hours) {
    setBusy('__snooze__')
    setSnoozeMsg('')
    try {
      const r = await snoozeMe(memberId, hours)
      if (r && r.ok === false) {
        if (r.error === 'too-early') setSnoozeMsg(`Snoozing opens at ${r.availableAt}.`)
        else if (r.error === 'no-snoozes-left') setSnoozeMsg(`You've used today's ${r.maxPerDay} snooze${r.maxPerDay === 1 ? '' : 's'} — this needs to be resolved now.`)
        else if (r.error === 'too-far-behind') setSnoozeMsg(`You're ${r.backlogDays} days behind — too far behind to snooze.`)
        else setSnoozeMsg(r.error || 'Could not snooze.')
      } else {
        recheck()
      }
    } finally { setBusy('') }
  }

  const isAdmin = isSeatLoaded() && hasModule('admin')
  const snoozeInfo = status.snoozeInfo || null
  // Round 17 — when snoozing isn't available, show a plain explanatory sentence instead of a
  // disabled-but-still-button-shaped control. A native `disabled` button silently swallows the
  // click with zero feedback — indistinguishable from broken. "Too early" wins outright: before
  // the earliest-snooze time, nothing else about eligibility (including the backlog cutoff) is
  // even relevant yet, so don't bury that under a different reason.
  let snoozeUnavailable = ''
  if (snoozeInfo && snoozeInfo.tooEarly) snoozeUnavailable = `Snoozing opens at ${snoozeInfo.earliestTime}.`
  else if (snoozeInfo && snoozeInfo.tooFarBehind) snoozeUnavailable = `You're ${snoozeInfo.backlogDays} days behind — too far behind to snooze.`
  else if (snoozeInfo && snoozeInfo.exhausted) snoozeUnavailable = `You've used today's ${snoozeInfo.maxPerDay} snooze${snoozeInfo.maxPerDay === 1 ? '' : 's'}.`

  return (
    <div style={S.scrim}>
      <div style={S.card}>
        {/* Staged: your own time card first; reviews only appear once your time is current. */}
        <RoleChip role={hasPersonal ? 'Everyone' : 'Time reviewer'} what={hasPersonal ? 'Your own time card' : (hasReviews ? 'Time and expenses you review' : 'Time that failed to post')} />
        <div style={S.eyebrow}>{hasPersonal ? 'Time entry required' : (hasReviews ? 'Reviews required' : 'Failed time posts')}</div>
        <h2 style={S.title}>
          {hasPersonal ? 'Record your time to continue' : (hasReviews ? 'Complete your reviews to continue' : 'Resolve failed time posts to continue')}
        </h2>
        <hr style={S.rule} />
        {hasPersonal && (
          <p style={S.body}>
            {mode === 'hours'
              ? 'Before proceeding, you must make your time card current.'
              : 'Before proceeding, you must make your time card current — record your time, or confirm you did not work.'}
          </p>
        )}

        {hasPersonal && <div style={S.list}>
          {personalDays.map((d) => (
            <div key={d.date} style={{ ...(idxByKey['p:' + d.date] === focusedIndex ? { ...S.row, ...S.rowFocused } : S.row), ...(mode === 'hours' ? null : { flexWrap: 'wrap' }) }}
              onMouseEnter={() => setFocusedIndex(idxByKey['p:' + d.date] ?? 0)}>
              <div style={S.rowMain}>
                <div style={S.rowDate}>{d.weekly ? fmtWeek(d.date) : fmtDay(d.date)}</div>
                {mode === 'hours' ? (
                  <div style={S.rowMeta}>
                    {hrs(d.submitted)} of {hrs(d.required)}h submitted{d.weekly ? ' this week' : ''}
                    {d.submitted >= d.required && d.required > 0 ? ' ✓' : ''}
                  </div>
                ) : (
                  <div style={S.rowMeta}>
                    {d.submitted > 0 ? `${hrs(d.submitted)}h submitted` : 'No time recorded'}
                  </div>
                )}
                {/* The blocker gets its own line, named and quantified. It used to be a muted
                    "· drafts not submitted" tacked onto the end of the hours line, which read as
                    self-contradictory ("8.1 of 8h submitted · drafts not submitted") — Brandon
                    reasonably concluded the gate was broken when he'd met the minimum but still had
                    one 0.9h entry sitting unsubmitted. See docs/TIMECARDS.md, 2026-08-04. */}
                {d.hasDrafts && !d.hasRejected && (
                  <div style={S.blocker}>
                    {d.pendingCount
                      ? `${hrs(d.pendingHours)}h in ${d.pendingCount} unsubmitted ${d.pendingCount === 1 ? 'entry' : 'entries'}. Submit to clear this day.`
                      : 'You have unsubmitted entries. Submit them to clear this day.'}
                  </div>
                )}
                {d.hasRejected && (
                  <div style={S.blocker}>An entry was sent back. Fix and resubmit it to clear this day.</div>
                )}
                {d.failedCount > 0 && (
                  <div style={S.blocker}>{`${hrs(d.failedHours)}h in ${d.failedCount} ${d.failedCount === 1 ? 'entry' : 'entries'} didn’t reach BQE Core, so ${d.failedCount === 1 ? 'it doesn’t' : 'they don’t'} count yet. Fix the phase or project and press Retry post to clear this day.`}</div>
                )}
              </div>

              <div style={S.rowActions}>
                {mode === 'hours' ? (
                  <button style={S.primary} onClick={() => openTimeCard(d.date)}>Open Time Card</button>
                ) : null}
              </div>
              {mode !== 'hours' && (() => {
                const day = d.weekly ? fmtWeek(d.date) : fmtDay(d.date)
                // TRIAGE TILES (2026-10-16, CLAUDE.md "Triage decisions"): the day's two answers.
                const opts = d.submitted > 0 ? [
                  { key: 'confirm', icon: 'lock', label: 'Confirm complete', sub: `That was all of it: ${hrs(d.submitted)}h`, tone: 'primary',
                    step: { title: `Confirm ${day} is complete`, body: `Locks ${day} at ${hrs(d.submitted)}h. Reopening it later takes an admin.`, confirm: () => 'Lock the day', confirmIcon: 'lock' },
                    run: async () => { const r = await confirmComplete(memberId, d.date); if (r && r.error) throw new Error(r.error) }, done: () => `${day} confirmed at ${hrs(d.submitted)}h.` },
                  { key: 'add', icon: 'timecard', label: 'Add time', sub: 'Something is missing', open: () => openTimeCard(d.date) },
                ] : [
                  { key: 'worked', icon: 'timecard', label: 'I worked', sub: 'Open the time card', tone: 'primary', open: () => openTimeCard(d.date) },
                  { key: 'off', icon: 'dayoff', label: "Didn't work", sub: 'A day off; nothing to log',
                    step: { title: `Mark ${day} as a day off`, body: `Nothing gets logged and ${day} locks. Reopening it later takes an admin.`, confirm: () => 'Mark it a day off', confirmIcon: 'dayoff' },
                    run: async () => { const r = await attestDay(memberId, d.date, 'off'); if (r && r.error) throw new Error(r.error) }, done: () => `${day} marked as a day off.` },
                ]
                return (
                  <div style={{ flexBasis: '100%' }}>
                    <TriageTiles options={opts} onConfirm={async (key) => { const o = opts.find(x => x.key === key); await o.run(); return o.done() }} onFolded={() => recheck()} />
                  </div>
                )
              })()}
            </div>
          ))}
        </div>}

        {hasPersonal && mode === 'attest' && (
          <p style={S.fine}>Confirming a day locks it. Reopening it later requires an admin.</p>
        )}

        {/* Reviewer stage — shown only once the member's own time card is current (hasPersonal false). */}
        {!hasPersonal && hasReviews && (
          <p style={S.body}>
            Before proceeding, approve or send back what your people sent in before today
            {hasTimeReviews ? ` — ${reviewerGate.total} time entr${reviewerGate.total === 1 ? 'y' : 'ies'}` : ''}
            {gateExp.length ? `${hasTimeReviews ? ' and' : ' —'} ${reviewerGate.expenses.total} expense${reviewerGate.expenses.total === 1 ? '' : 's'}` : ''}. (Today's don't block you.)
          </p>
        )}
        {!hasPersonal && hasReviews && (
          <div style={S.list}>
            {reviewerGate.byMember.map((m) => {
              const ex = expOf(m)
              return (
              <div key={m.memberId || m.memberName}
                style={idxByKey['r:' + (m.memberId || m.memberName)] === focusedIndex ? { ...S.row, ...S.rowFocused } : S.row}
                onMouseEnter={() => setFocusedIndex(idxByKey['r:' + (m.memberId || m.memberName)] ?? 0)}>
                <div style={S.rowMain}>
                  <div style={S.rowDate}>{m.memberName}</div>
                  <div style={S.rowMeta}>
                    {m.count} time entr{m.count === 1 ? 'y' : 'ies'} to review{m.oldestDate ? ' · since ' + fmtDay(m.oldestDate) : ''}
                  </div>
                  {ex && <div style={S.rowMeta}>{expLine(ex)}</div>}
                </div>
                <div style={S.rowActions}>
                  {ex && <button style={S.ghost} onClick={() => openExpenseReview(m)}>Review expenses</button>}
                  <button style={S.primary} onClick={() => openTimeReview(m, ['Submitted'], { from: m.oldestDate })}>Review time</button>
                </div>
              </div>
              )
            })}
            {/* People with only expenses waiting. */}
            {expOnly.map(ex => (
              <div key={'x:' + (ex.memberId || ex.memberName)}
                style={idxByKey['x:' + ex.memberId] === focusedIndex ? { ...S.row, ...S.rowFocused } : S.row}
                onMouseEnter={() => setFocusedIndex(idxByKey['x:' + ex.memberId] ?? 0)}>
                <div style={S.rowMain}>
                  <div style={S.rowDate}>{ex.memberName}</div>
                  <div style={S.rowMeta}>{expLine(ex)}</div>
                </div>
                <div style={S.rowActions}>
                  <button style={S.primary} onClick={() => openExpenseReview(ex)}>Review expenses</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Post-failed stage — approved entries that never reached BQE Core. Surfaced on the
            responsible reviewer's lockout so a failed post can't silently slip through unbilled. */}
        {!hasPersonal && hasPostFailed && (
          <p style={{ ...S.body, color: '#c0392b' }}>
            {postFailed.total} approved time {postFailed.total === 1 ? 'entry' : 'entries'} failed to post to BQE Core and must be retried — until fixed, {postFailed.total === 1 ? "it won't" : "they won't"} be billed.
          </p>
        )}
        {!hasPersonal && hasPostFailed && (
          <div style={S.list}>
            {postFailed.byMember.map((m) => (
              <div key={'pf-' + (m.memberId || m.memberName)}
                style={idxByKey['pf:' + (m.memberId || m.memberName)] === focusedIndex ? { ...S.row, ...S.rowFocused } : S.row}
                onMouseEnter={() => setFocusedIndex(idxByKey['pf:' + (m.memberId || m.memberName)] ?? 0)}>
                <div style={S.rowMain}>
                  <div style={S.rowDate}>{m.memberName}</div>
                  <div style={S.rowMeta}>
                    <span style={{ color: '#c0392b' }}>{m.count} failed post{m.count === 1 ? '' : 's'}</span>{m.oldestDate ? ' · since ' + fmtDay(m.oldestDate) : ''}
                  </div>
                </div>
                <div style={S.rowActions}>
                  <button style={S.primary} onClick={() => openTimeReview(m, ['Post Failed'], { from: m.oldestDate })}>Retry in Time Review</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {snoozeMsg && <div style={S.snoozeMsg}>{snoozeMsg}</div>}
        <div style={S.footer}>
          <button style={S.linkBtn} onClick={() => signOut()}>Sign out</button>
          {snoozeUnavailable ? (
            <span style={S.snoozeUnavailable}>{snoozeUnavailable}</span>
          ) : (
            <button style={S.linkBtn} onClick={() => doSnooze(1)} disabled={busy === '__snooze__'}>
              {busy === '__snooze__' ? '…' : 'Snooze 1 hour'}
            </button>
          )}
          {isAdmin && (
            <button style={S.linkBtn} onClick={() => doPause(4)} disabled={busy === '__pause__'}>
              {busy === '__pause__' ? 'Pausing…' : 'Pause 4 hours (admin)'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const S = {
  scrim: {
    position: 'fixed', inset: 0, zIndex: 1000000,   // above all chrome; Time Card (.tc-overlay) sits one above
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    background: 'rgba(4,21,24,0.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  },
  card: {
    width: '100%', maxWidth: 540, maxHeight: 'calc(100vh - 48px)', background: 'rgba(255,255,255,0.97)',
    border: '0.5px solid rgba(255,255,255,0.6)', borderRadius: 16,
    boxShadow: '0 24px 64px rgba(4,21,24,0.5)', padding: 'clamp(28px, 4vw, 44px)',
    display: 'flex', flexDirection: 'column', minHeight: 0,
  },
  eyebrow: { flexShrink: 0, fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#c0392b', marginBottom: 14 },
  title: { flexShrink: 0, fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(18px, 2.4vw, 24px)', fontWeight: 300, letterSpacing: '0.03em', color: '#07272D', lineHeight: 1.3, margin: 0 },
  rule: { flexShrink: 0, height: '0.5px', background: 'rgba(7,39,45,0.12)', border: 'none', margin: '20px 0', width: 60 },
  body: { flexShrink: 0, fontFamily: '"DM Sans", sans-serif', fontSize: 14, lineHeight: 1.6, color: '#3a5260', margin: '0 0 20px' },
  // Scrollable so a long list of outstanding days/weeks never runs off the page.
  list: { display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', flex: '1 1 auto', minHeight: 0, marginRight: -8, paddingRight: 8 },
  row: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '12px 14px', borderRadius: 10, border: '0.5px solid rgba(7,39,45,0.12)', background: '#f4f7f8',
  },
  // Left column flexes + shrinks (meta wraps within it); the action column is pinned right and never
  // wraps, so the buttons stay in the same place on every row.
  rowFocused: { borderColor: '#07272D', boxShadow: '0 0 0 2px rgba(7,39,45,0.22)', background: '#eef3f5' },
  rowMain: { flex: '1 1 auto', minWidth: 0 },
  rowDate: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, color: '#07272D' },
  rowMeta: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: '#5b8494', marginTop: 3 },
  // What's actually standing in the way, on its own line and not muted — the hours line above it is
  // context, this is the instruction. Replaced the old inline `rejectFlag` span.
  blocker: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, color: '#b45309', marginTop: 4, lineHeight: 1.4 },
  rowActions: { display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 },
  primary: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#fff', background: '#07272D',
    border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer',
  },
  ghost: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 500, color: '#07272D', background: 'transparent',
    border: '0.5px solid rgba(7,39,45,0.3)', borderRadius: 6, padding: '8px 12px', cursor: 'pointer',
  },
  sectionLabel: { flexShrink: 0, fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#5b8494', margin: '20px 0 10px' },
  fine: { flexShrink: 0, fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#7a9aaa', margin: '18px 0 0', textAlign: 'center' },
  snoozeMsg: { flexShrink: 0, fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#b45309', margin: '14px 0 0', textAlign: 'center' },
  // Deliberately NOT button/link-styled — no underline, muted, default cursor. This replaces a
  // disabled <button>, which silently swallowed clicks with zero feedback (indistinguishable from
  // broken). This is a status sentence, not something to click.
  snoozeUnavailable: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#9ab0b8', cursor: 'default' },
  footer: { flexShrink: 0, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 16, marginTop: 22 },
  linkBtn: {
    fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#7a9aaa',
    background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline',
  },
}
